import BugsnagPerformance from "@bugsnag/browser-performance";
import Bugsnag from "@bugsnag/js";
import { hasAnalyticsConsent } from "@/utils/consent/consent";
import { version } from "../../../package.json";

const VITE_ENV = import.meta.env.VITE_ENV;

let started = false;
let deliveryFilter = null;

/**
 * Inicia Bugsnag (errores + rendimiento) solo en producción y si el usuario
 * ha aceptado las cookies no exentas. Debe llamarse en el boot y al aceptar
 * el banner. No-op en el resto de casos.
 */
export function maybeStartBugsnag() {
	if (VITE_ENV !== "pro") return;
	if (!hasAnalyticsConsent()) return;

	const apiKey = import.meta.env.VITE_BUGSNAG_APIKEY;

	if (!started) {
		Bugsnag.start({
			apiKey,
			appVersion: version,
		});
		BugsnagPerformance.start({ apiKey });

		started = true;
	}

	// Si el consentimiento se retiró y luego se re-aceptó, quita el filtro
	// que bloqueaba el envío de eventos.
	if (deliveryFilter) {
		Bugsnag.removeOnError(deliveryFilter);
		deliveryFilter = null;
	}
}

/**
 * @returns {boolean}
 */
export function isBugsnagStarted() {
	return started;
}

/**
 * Notifica un error a Bugsnag. No-op si no está iniciado.
 * @param {Error} error
 * @param {(event: import("@bugsnag/js").Event) => void} [onError]
 */
export function notifyBugsnag(error, onError) {
	if (!started) return;
	Bugsnag.notify(error, onError);
}

/**
 * Asocia el usuario a los eventos de Bugsnag. No-op si no está iniciado.
 * @param {string} [id]
 * @param {string} [email]
 * @param {string} [username]
 */
export function setBugsnagUser(id, email, username) {
	if (!started) return;

	Bugsnag.setUser(id, email, username);
}

/**
 * Desvincula el usuario de Bugsnag (p. ej. al cerrar sesión). No-op si no está iniciado.
 */
export function clearBugsnagUser() {
	if (!started) return;

	Bugsnag.setUser();
}

/**
 * Retirada de consentimiento: Bugsnag no expone un stop, así que desvinculamos
 * al usuario y bloqueamos el envío de nuevos eventos de error con un filtro.
 * BugsnagPerformance no expone stop; sus spans en vuelo pueden enviarse hasta
 * la próxima recarga de página. No-op si nunca se inició.
 */
export function stopBugsnagSession() {
	if (!started) return;

	Bugsnag.setUser();
	if (!deliveryFilter) {
		deliveryFilter = () => false;
		Bugsnag.addOnError(deliveryFilter);
	}
}
